import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { moderationService } from '../services/moderationService';
import { ArrowLeft, Flag } from 'lucide-react';

const ReportDetailView: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { select, locale } = useLanguage();
  const [report, setReport] = useState<Record<string, unknown> | null>(null);
  const [actions, setActions] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (!reportId) throw new Error('Missing report id');
        const rep = await moderationService.getReportById(reportId);
        const acts = await moderationService.getActionsForReport(reportId);
        setReport(rep as Record<string, unknown>);
        setActions(acts as Record<string, unknown>[]);
      } catch (err: unknown) {
        console.error('Error loading report:', err);
        const message =
          err instanceof Error
            ? err.message
            : select({ es: 'No se pudo cargar el reporte.', en: 'Failed to load report.' });
        setError(message);
      } finally {
        setLoading(false);
      }
    })();
  }, [reportId, select]);

  const formatDateTime = (value: string) => new Date(value).toLocaleString(locale, { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });

  if (loading) {
    return <div className="p-6 max-w-4xl mx-auto"><div className="animate-pulse space-y-4"><div className="h-24 bg-gray-200 rounded-xl"/><div className="h-24 bg-gray-200 rounded-xl"/></div></div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h2 className="text-xl font-bold text-red-900 mb-2">{select({ es: 'Error', en: 'Error' })}</h2>
          <p className="text-red-700">{error}</p>
          <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            <ArrowLeft size={20} className="inline-block mr-2" /> {select({ es: 'Volver', en: 'Go back' })}
          </button>
        </div>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors">
        <ArrowLeft size={20} /> {select({ es: 'Volver', en: 'Back' })}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <Flag className="text-red-600" />
          <h1 className="text-xl font-semibold text-gray-900">{select({ es: 'Detalle del reporte', en: 'Report detail' })}</h1>
        </div>
        <p className="text-sm text-gray-500 mb-1">{select({ es:'ID:', en:'ID:' })} {report.id}</p>
        <p className="text-sm text-gray-500 mb-1">{select({ es:'Estado:', en:'Status:' })} {report.status}</p>
        <p className="text-sm text-gray-500 mb-1">{select({ es:'Creado:', en:'Created:' })} {formatDateTime(report.created_at)}</p>
        <p className="text-sm text-gray-500 mb-1">{select({ es:'Reportado por:', en:'Reported by:' })} {report.reporter?.full_name || report.reporter?.username || report.reporter_id}</p>
        <p className="text-gray-800 mt-3"><strong>{select({ es:'Motivo:', en:'Reason:' })}</strong> {report.reason}</p>
        {report.description && (<p className="text-gray-700 mt-1">{report.description}</p>)}
        {report.reported_profile && (<p className="text-sm text-gray-500 mt-2">{select({ es:'Perfil reportado:', en:'Reported profile:' })} @{report.reported_profile.username}</p>)}
        {report.reported_post && (<p className="text-sm text-gray-500 mt-2">{select({ es:'Publicación:', en:'Post:' })} {report.reported_post.title}</p>)}
        {report.reported_comment && (<p className="text-sm text-gray-500 mt-2">{select({ es:'Comentario:', en:'Comment:' })} "{report.reported_comment.content?.slice(0,120)}"</p>)}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{select({ es:'Historial de acciones', en:'Actions history' })}</h2>
        <div className="space-y-3">
          {actions.length === 0 && (
            <p className="text-gray-500">{select({ es:'Sin acciones registradas.', en:'No actions recorded.' })}</p>
          )}
          {actions.map((a) => (
            <div key={a.id} className="border border-gray-200 rounded-lg p-3 flex items-start justify-between">
              <div>
                <p className="text-gray-900"><strong>{a.action}</strong> {a.reason ? `— ${a.reason}` : ''}</p>
                {a.moderator && (
                  <p className="text-sm text-gray-500">{select({ es:'Por:', en:'By:' })} {a.moderator.full_name || a.moderator.username}</p>
                )}
              </div>
              <div className="text-sm text-gray-500">{formatDateTime(a.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportDetailView;

